import globalRenderer from '@/shared/misc/global-renderer'
import PostEffectManager from './posteffects/posteffects-manager'
import {
  Clock,
  Color,
  Fog,
  Mesh,
  Object3D,
  OneMinusDstAlphaFactor,
  PerspectiveCamera,
  PointLight,
  Scene,
  ShaderMaterial
} from 'three'
import sizeSystem from '@/shared/size-system'
import assetsSystem from '@/shared/assets-system'
import coinFactory from '@/shared/common/coin-factory'
import CoinDefinition from '@/shared/common/coin-definition'
import coinMaterial from './materials/coin-material'
import soundSystem from '@/shared/sound-system'
import query from '@/shared/misc/query'
import Denomination from '@/shared/common/denomination'

export default class CoinEffects {
  private running = true
  private clock = new Clock()
  private time = 0
  private renderer
  private postEffectManager
  private scene
  private camera
  private bloomListIsDirty = true
  private coin: Mesh | undefined

  constructor() {
    this.renderer = globalRenderer.getRenderer()
    this.postEffectManager = new PostEffectManager(this.renderer)
    this.scene = new Scene()
    this.scene.background = new Color(0x000000)
    this.scene.fog = new Fog(0x000000, 7, 12)
    this.camera = new PerspectiveCamera(53, window.innerWidth / window.innerHeight, 0.1, 150)
    this.postEffectManager.setSceneAndCamera(this.scene, this.camera)
    this.postEffectManager.updateBloomList()

    this.camera.position.z = 5
    this.camera.position.y = 0
    this.camera.rotation.x = 0

    const light = new PointLight(0xffffff, 10.0)
    light.position.set(1, 0, 5)
    //this.scene.add(light)

    const d = query.get('d') as string
    let finalD
    if (d) {
      let dAsNumber = parseInt(d)
      if (dAsNumber < 100) {
        dAsNumber = 100
      } else if (dAsNumber > 400000) {
        dAsNumber = 400000
      }
      finalD = dAsNumber as Denomination
    } else {
      finalD = Denomination.Ten
    }

    const definition = coinFactory.getCoinDefinition(finalD)
    assetsSystem.loadSingleCoin(definition).then(() => {
      const model = assetsSystem.getModel(definition.modelName) as Object3D

      this.processCoin(model, definition)

      this.bloomListIsDirty = true

      this.gameLoop()
    })

    soundSystem.changeAmbience(['ambience_vault'])

    sizeSystem.onResize.add(this.resize)
    this.resize()
  }

  private processCoin(model: Object3D, definition: CoinDefinition) {
    const loadedMesh = model.clone().children.filter((x: Object3D) => x.type === 'Mesh')[0] as Mesh
    const mat = coinMaterial.getMaterial(definition.normalMap, definition.diffuseMap, definition.gemColor)
    const geo = loadedMesh.geometry
    geo.scale(100, 100, 100)

    const coinMesh = new Mesh(loadedMesh.geometry, mat)
    coinMesh.rotation.x = Math.PI * 0.5
    coinMesh.castShadow = false
    coinMesh.receiveShadow = false
    coinMesh.userData.enableBloom = true

    this.scene.add(coinMesh)

    coinMesh.material = mat
    this.coin = coinMesh
  }

  private gameLoop = () => {
    if (this.running) {
      requestAnimationFrame(this.gameLoop)
    }

    let delta = this.clock.getDelta()

    if (delta > 0.027) {
      delta = 0.027
    }

    this.time += delta
    this.update(this.time, delta)
  }

  update(time: number, delta: number): void {
    if (this.coin) {
      this.coin.rotation.x = Math.sin(0.4 * time) * 0.3 + Math.PI * 0.5
      this.coin.rotation.z = Math.sin(0.8 * time) * 0.1
      this.coin.rotation.y = Math.sin(0.3 * time) * 0.1
      ;(this.coin.material as ShaderMaterial).userData.shader.uniforms.time.value = time
    }

    if (globalRenderer.usePostEffects) {
      if (this.bloomListIsDirty) {
        this.bloomListIsDirty = false
        this.postEffectManager.updateBloomList()
      }

      this.postEffectManager.render(time, delta)
    } else {
      this.renderer.render(this.scene, this.camera)
    }

    this.renderer.info.reset()
  }

  resize = () => {
    const size = sizeSystem.size
    this.renderer.setSize(size.width, size.height)

    this.postEffectManager.resize()

    const cameraAsPerspectiveType = this.camera as PerspectiveCamera
    cameraAsPerspectiveType.aspect = size.width / size.height
    cameraAsPerspectiveType.updateProjectionMatrix()
  }
}
